import React, { useState, useEffect, useRef, useCallback } from "react";
import { Col, Row, Button } from "reactstrap";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import moment from "moment";

import DayPicker from "../components/DayPicker";
import Calendar from "../components/Calendar";
import Layout from "../components/Layout";
import api from "../services/api";
import { getWeekStart, toDate } from "../utils/date";

const DEFAULT_SERVICE = { id: 0, title: "", duration: 60 };

const BookNow = () => {
  const calendarRef = useRef(null);
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [weekStart, setWeekStart] = useState(() => getWeekStart(new Date()));
  const [hours, setHours] = useState(null);
  const [refreshToken, setRefreshToken] = useState(0);
  const [selectedStartTime, setSelectedStartTime] = useState("");
  const [selectedService, setSelectedService] = useState({ ...DEFAULT_SERVICE });

  const fetchHours = useCallback(async (date) => {
    try {
      const res = await api.get(
        `/business-hours/?date=${moment(date).format("YYYY-MM-DD")}`
      );
      setHours(res.data);
    } catch (error) {
      console.error("Failed to load business hours:", error);
    }
  }, []);

  useEffect(() => {
    fetchHours(selectedDate);
  }, [selectedDate, fetchHours]);

  useEffect(() => {
    if (calendarRef.current && typeof calendarRef.current.getApi === "function") {
      calendarRef.current.getApi().gotoDate(selectedDate);
    }
  }, [selectedDate]);

  const resetSelections = useCallback(() => {
    setSelectedStartTime("");
    setSelectedService({ ...DEFAULT_SERVICE });
  }, []);

  const handleDateChange = useCallback(
    (date) => {
      const normalizedDate = toDate(date);
      setSelectedDate(normalizedDate);
      setWeekStart(getWeekStart(normalizedDate));
      resetSelections();
    },
    [resetSelections]
  );

  const handleToday = useCallback(() => {
    handleDateChange(new Date());
  }, [handleDateChange]);

  const changeWeek = useCallback(
    (offset) => {
      const next = new Date(weekStart);
      next.setDate(next.getDate() + offset * 7);
      handleDateChange(next);
    },
    [weekStart, handleDateChange]
  );

  const triggerRefresh = useCallback(() => {
    setRefreshToken((value) => value + 1);
  }, []);

  const handleCalendarSubmit = useCallback(
    (date) => {
      if (date) {
        handleDateChange(date);
      }
      triggerRefresh();
    },
    [handleDateChange, triggerRefresh]
  );

  return (
    <Layout>
      <Row className="m-0 flex-grow-1 h-100">
        <Col>
          <Row className="justify-content-center bg-white p-2">
            <Col xs="1" className="d-flex justify-content-start m-0 p-0">
              <Button color="secondary" onClick={() => changeWeek(-1)}>
                <FaChevronLeft />
              </Button>
            </Col>
            <Col xs="10" className="d-flex justify-content-center m-0 p-0">
              <DayPicker
                startDate={weekStart}
                currentDate={selectedDate}
                onDateSelected={handleDateChange}
              />
            </Col>
            <Col xs="1" className="d-flex justify-content-end m-0 p-0">
              <Button color="secondary" onClick={() => changeWeek(1)}>
                <FaChevronRight />
              </Button>
            </Col>
          </Row>

          <Row className="border-start">
            <Calendar
              calendarRef={calendarRef}
              selectedDate={selectedDate}
              hours={hours}
              bookingDate={selectedDate}
              bookingTime={selectedStartTime}
              service={selectedService}
              onToday={handleToday}
              onSubmit={handleCalendarSubmit}
              refreshToken={refreshToken}
            />
          </Row>
        </Col>
      </Row>
    </Layout>
  );
};

export default BookNow;
